import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Paper,
  TextField,
  Button,
  Stack,
  Alert,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { addMinutes } from 'date-fns';
import DeleteIcon from '@mui/icons-material/Delete';
import { 
  updateTimeslot, 
  getTimeslot,
  getJob,
  deleteTimeslot
} from '../services/api';
import { Job } from '../models/types';

const EditTimeslotPage: React.FC = () => {
  const navigate = useNavigate();
  const { timeslotId } = useParams<{ timeslotId: string }>();
  
  const [job, setJob] = useState<Job | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    startTime: new Date(),
    endTime: addMinutes(new Date(), 60),
    requiredVolunteers: 1,
    goodieThreshold: 60, // Default 60 minutes
    description: '',
    jobId: '',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  
  // Add state for delete confirmation dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      if (!timeslotId) return;
      
      try {
        setLoading(true);
        const timeslot = await getTimeslot(timeslotId);
        
        const jobData = await getJob(timeslot.jobId);
        setJob(jobData);
        
        setFormData({
          startTime: new Date(timeslot.startTime),
          endTime: new Date(timeslot.endTime),
          requiredVolunteers: timeslot.requiredVolunteers,
          goodieThreshold: timeslot.goodieThreshold || 60,
          description: timeslot.description || '',
          jobId: timeslot.jobId,
        });
      } catch (err) {
        console.error('Error fetching timeslot details:', err);
        setLoadError('Zeitfenster-Details konnten nicht geladen werden. Bitte versuchen Sie es später erneut.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [timeslotId]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    if (errors[name]) {
      setErrors({ ...errors, [name]: '' });
    }
  };

  const handleNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    const parsedValue = parseInt(value, 10);
    if (!isNaN(parsedValue) && parsedValue > 0) {
      setFormData({ ...formData, [name]: parsedValue });
      if (errors[name]) {
        setErrors({ ...errors, [name]: '' });
      }
    }
  };

  const handleStartTimeChange = (dateTime: Date | null) => {
    if (dateTime) {
      setFormData({ 
        ...formData, 
        startTime: dateTime
      });
      
      // Clear error
      if (errors.startTime) {
        setErrors({ ...errors, startTime: '' });
      }
    }
  };

  const handleEndTimeChange = (dateTime: Date | null) => {
    if (dateTime) {
      setFormData({ ...formData, endTime: dateTime });
      
      // Clear error
      if (errors.endTime) {
        setErrors({ ...errors, endTime: '' });
      }
    }
  };
  const validate = () => {
    const newErrors: Record<string, string> = {};
      if (formData.startTime >= formData.endTime) {
      newErrors.endTime = 'Die Endzeit muss nach der Startzeit liegen';
    }
    
    if (formData.requiredVolunteers <= 0) {
      newErrors.requiredVolunteers = 'Die Anzahl der benötigten Helfer muss größer als 0 sein';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!timeslotId) return;
    if (!validate()) return;
    
    setIsSubmitting(true);
    setSubmitError(null);
    
    try {
      await updateTimeslot(timeslotId, {
        startTime: formData.startTime.toISOString(),
        endTime: formData.endTime.toISOString(),
        requiredVolunteers: formData.requiredVolunteers,
        goodieThreshold: 60,
        description: formData.description,
      });
      
      navigate(`/admin/jobs/${formData.jobId}`);
    } catch (err) {
      console.error('Failed to update timeslot:', err);
      setSubmitError('Zeitfenster konnte nicht aktualisiert werden. Bitte versuchen Sie es erneut.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Add function to handle delete dialog open
  const handleOpenDeleteDialog = () => {
    setDeleteDialogOpen(true);
    setDeleteError(null);
  };

  // Add function to handle delete dialog close
  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
  };

  // Add function to handle timeslot deletion
  const handleDeleteTimeslot = async () => {
    if (!timeslotId) return;
    
    setIsDeleting(true);
    setDeleteError(null);
    
    try {
      await deleteTimeslot(timeslotId);
      setDeleteDialogOpen(false);
      
      // Navigate back to job detail page
      navigate(`/admin/jobs/${formData.jobId}`);
    } catch (err) {
      console.error('Failed to delete timeslot:', err);
      setDeleteError('Zeitfenster konnte nicht gelöscht werden. Bitte versuchen Sie es erneut.');
    } finally {
      setIsDeleting(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ mt: 4, textAlign: 'center' }}>
        <CircularProgress />
        <Typography variant="body1" sx={{ mt: 2 }}>
          Lade Zeitfenster-Details...
        </Typography>
      </Container>
    );
  }

  if (loadError) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Alert severity="error">{loadError}</Alert>
        <Box sx={{ mt: 2, textAlign: 'center' }}>
          <Button variant="contained" onClick={() => navigate('/admin')}>
            Back to Admin Dashboard
          </Button>
        </Box>
      </Container>
    );
  }

  if (!job) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>        <Alert severity="error">Job nicht gefunden</Alert>
        <Box sx={{ mt: 2, textAlign: 'center' }}>
          <Button variant="contained" onClick={() => navigate('/admin')}>
            Zurück zum Admin-Dashboard
          </Button>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="md">
      <Box sx={{ mt: 4, mb: 4 }}>
        <Typography variant="h4" gutterBottom>          
          Zeitfenster bearbeiten für {job?.name}
        </Typography>
        
        <Paper sx={{ p: 3 }}>
          {submitError && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {submitError}
            </Alert>
          )}
          
          <Box component="form" onSubmit={handleSubmit} noValidate>
            <Stack spacing={3}>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                <Box sx={{ flex: 1 }}>
                  <DateTimePicker
                    label="Startzeit"
                    value={formData.startTime}
                    onChange={handleStartTimeChange}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        required: true,
                        error: !!errors.startTime,
                        helperText: errors.startTime,
                      }
                    }}
                  />
                </Box>
                
                <Box sx={{ flex: 1 }}>
                  <DateTimePicker
                    label="Endzeit"
                    value={formData.endTime}
                    onChange={handleEndTimeChange}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        required: true,
                        error: !!errors.endTime,
                        helperText: errors.endTime,
                      }
                    }}
                  />
                </Box>
              </Stack>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                <Box sx={{ flex: 1 }}>
                  <TextField
                    required
                    fullWidth
                    id="requiredVolunteers"
                    label="Benötigte Helfer"
                    name="requiredVolunteers"
                    type="number"
                    InputProps={{ inputProps: { min: 1 } }}
                    value={formData.requiredVolunteers}
                    onChange={handleNumberChange}
                    error={!!errors.requiredVolunteers}
                    helperText={errors.requiredVolunteers}
                  />
                </Box>
                
               
              </Stack>
              
              <Box>
                <TextField
                  fullWidth
                  id="description"
                  label="Beschreibung (Optional)"
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                />
              </Box>
              
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'space-between' }}>
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<DeleteIcon />}
                  onClick={handleOpenDeleteDialog}
                  disabled={isSubmitting}
                >
                  Zeitfenster löschen
                </Button>
              
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Button
                    variant="outlined"
                    onClick={() => navigate(`/admin/jobs/${formData.jobId}`)}
                    disabled={isSubmitting}
                  >
                    Abbrechen
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? 'Speichert...' : 'Änderungen speichern'}
                  </Button>
                </Box>
              </Box>
            </Stack>
          </Box>
        </Paper>
      </Box>
      
      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleCloseDeleteDialog}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">
          Zeitfenster löschen
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            Sind Sie sicher, dass Sie dieses Zeitfenster löschen möchten? Alle zugehörigen Anmeldungen werden ebenfalls gelöscht. Diese Aktion kann nicht rückgängig gemacht werden.
          </DialogContentText>
          
          {deleteError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {deleteError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={handleCloseDeleteDialog} 
            disabled={isDeleting}
          >
            Abbrechen
          </Button>
          <Button 
            onClick={handleDeleteTimeslot} 
            color="error" 
            disabled={isDeleting}
            autoFocus
          >
            {isDeleting ? 'Wird gelöscht...' : 'Löschen'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default EditTimeslotPage;

